// pages/auth/Validation.js - COMPLETE READY-TO-PASTE VERSION WITH FIXES
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

export default function Validation() {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState('');
  
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // Get email and business name from navigation state
  const email = location.state?.email || '';
  const businessName = location.state?.businessName || '';
  
  // Refs for OTP inputs
  const inputRefs = useRef([]);

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      navigate('/auth/signup', { replace: true });
    }
  }, [email, navigate]);

  // Countdown timer for resend
  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Only take last character
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields filled
    if (newOtp.every(digit => digit !== '') && index === 5) {
      handleVerifyOtp(newOtp.join(''));
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste - FIXED to remove all whitespace and non-digits
  const handlePaste = (e) => {
    e.preventDefault();
    
    // 🔧 FIX: Remove all whitespace and non-digits before processing
    const pastedData = e.clipboardData.getData('text')
      .replace(/\s/g, '')  // Remove ALL whitespace (spaces, tabs, newlines)
      .replace(/\D/g, '')  // Remove ALL non-digit characters
      .slice(0, 6);
    
    console.log('📋 Pasted OTP (cleaned):', pastedData);
    
    if (pastedData.length === 0) return;

    const newOtp = pastedData.split('');
    while (newOtp.length < 6) newOtp.push('');
    setOtp(newOtp);

    // Focus last filled input or last input
    const lastFilledIndex = Math.min(pastedData.length, 5);
    inputRefs.current[lastFilledIndex]?.focus();

    // Auto-submit if complete
    if (pastedData.length === 6) {
      handleVerifyOtp(pastedData);
    }
  };

  // Verify OTP - FIXED to trim and normalize
  const handleVerifyOtp = async (otpCode = otp.join('')) => {
    // 🔧 FIX: Trim whitespace from OTP
    otpCode = otpCode.trim();
    
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      console.log('🔍 Verifying OTP for:', email);
      console.log('🔍 OTP Code:', otpCode);
      console.log('🔍 OTP Length:', otpCode.length);
      
      // 🔧 FIX: Normalize email before sending
      const normalizedEmail = email.trim().toLowerCase();
      const response = await authAPI.verifyOtp(normalizedEmail, otpCode);
      
      console.log('✅ OTP Verification Response:', response);
      
      if (response.success) {
        toast.success('Email verified successfully!');
        
        // CRITICAL FIX: Pass token and user object correctly
        if (response.token && response.user) {
          console.log('🔐 Logging in with token and user:', {
            token: response.token.substring(0, 20) + '...',
            user: response.user
          });
          
          // Call login with (token, userObject)
          login(response.token, response.user);
          
          console.log('✅ Login successful, navigating to dashboard');
          
          // Navigate to dashboard
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 500);
        } else {
          console.warn('⚠️ No token/user in response, redirecting to login');
          toast.info('Please login with your credentials');
          navigate('/auth/login', { replace: true });
        }
      }
    } catch (error) {
      console.error('❌ OTP Verification Error:', error);
      const errorMessage = error.error || error.message || 'Invalid OTP. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      
      // Clear OTP on error
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  // Resend OTP - FIXED to normalize email
  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;

    setIsResending(true);
    setError('');

    try {
      console.log('📧 Resending OTP to:', email);
      
      // 🔧 FIX: Normalize email before sending
      const normalizedEmail = email.trim().toLowerCase();
      const response = await authAPI.resendOtp(normalizedEmail);
      
      if (response.success) {
        // If already verified (dev mode), login directly
        if (response.alreadyVerified && response.token && response.user) {
          toast.success('Email already verified! Logging you in...');
          login(response.token, response.user);
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 300);
          return;
        }

        toast.success('New OTP sent to your email!');
        setResendCooldown(60); // 60 second cooldown

        // Clear current OTP
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error('❌ Resend OTP Error:', error);
      const errorMessage = error.error || error.message || 'Failed to resend OTP. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  if (!email) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Panel - Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 p-12 flex-col justify-center items-center relative overflow-hidden">
        {/* Animated Grid Background */}
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(rgba(148, 163, 184, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(148, 163, 184, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '30px 30px',
              animation: 'gridFloat 20s ease-in-out infinite'
            }}
          />
        </div>

        {/* Floating Gradient Orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-purple-600/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center space-y-8">
          {/* Main Heading */}
          <div className="space-y-4">
            <h1 className="text-5xl font-bold text-white leading-tight">
              Digital Trent
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto rounded-full"></div>
            <p className="text-xl text-slate-300 max-w-md font-light">
              Your online AI Marketing and<br/>Lead management platform
            </p>
          </div>

          {/* Tagline */}
          <div className="pt-6">
            <p className="text-sm text-slate-400 font-medium uppercase tracking-wider">
              Powered by Artificial Intelligence
            </p>
          </div>
        </div>

        {/* CSS Animation for Grid */}
        <style>{`
          @keyframes gridFloat {
            0%, 100% {
              transform: translate(0, 0);
            }
            25% {
              transform: translate(10px, -10px);
            }
            50% {
              transform: translate(-5px, 5px);
            }
            75% {
              transform: translate(5px, 10px);
            }
          }
        `}</style>
      </div>

      {/* Right Panel - OTP Verification Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Verify Your Email
            </h2>
            <p className="text-sm text-gray-600">
              Enter the 6-digit code sent to
            </p>
            <p className="text-sm font-medium text-blue-600 mt-1">
              {email}
            </p>
          </div>

          {/* Business Name Display */}
          {businessName && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center">
                <i className="fas fa-building text-blue-600 mr-2"></i>
                <div>
                  <p className="text-xs text-gray-600">Account for</p>
                  <p className="text-sm font-medium text-gray-900">{businessName}</p>
                </div>
              </div>
            </div>
          )}

          {/* OTP Form Card */}
          <div className="bg-white border border-gray-300 rounded-lg shadow-sm p-6">
            <form onSubmit={(e) => { e.preventDefault(); handleVerifyOtp(); }} className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <i className="fas fa-exclamation-triangle text-red-600 mr-2 text-sm"></i>
                    <p className="text-xs text-red-600">{error}</p>
                  </div>
                </div>
              )}

              {/* OTP Input Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                  Enter Verification Code
                </label>
                <div className="flex justify-center gap-2">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      disabled={isVerifying}
                      className={`w-12 h-12 text-center text-xl font-bold border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all ${
                        error ? 'border-red-300' : 'border-gray-300'
                      } ${digit ? 'border-blue-600' : ''}`}
                      autoComplete="off"
                    />
                  ))}
                </div>
              </div>

              {/* Verify Button */}
              <button
                type="submit"
                disabled={isVerifying || otp.some(digit => !digit)}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2.5 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {isVerifying ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <i className="fas fa-check-circle mr-2"></i>
                    Verify Email
                  </div>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white text-gray-500">
                  Didn't receive the code?
                </span>
              </div>
            </div>

            {/* Resend Button */}
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={isResending || resendCooldown > 0}
              className="w-full bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 text-gray-700 disabled:text-gray-400 font-medium py-2.5 px-4 rounded-lg transition-colors"
            >
              {isResending ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-gray-400/30 border-t-gray-400 rounded-full animate-spin mr-2"></div>
                  Sending...
                </div>
              ) : resendCooldown > 0 ? (
                <div className="flex items-center justify-center">
                  <i className="fas fa-clock mr-2"></i>
                  Resend in {resendCooldown}s
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <i className="fas fa-paper-plane mr-2"></i>
                  Resend Code
                </div>
              )}
            </button>

            {/* Help Text */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                Check your spam folder if you don't see the email
              </p>
            </div>
          </div>

          {/* Footer Info */}
          <div className="mt-5 text-center">
            <p className="text-xs text-gray-500 mb-2">
              Need help?{' '}
              <a href="mailto:support@digitaltrent.co.uk" className="text-blue-600 hover:text-blue-700 underline">
                Contact support
              </a>
            </p>
            <Link
              to="/auth/login"
              className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              <i className="fas fa-arrow-left mr-1"></i>
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}